// Single source of truth for the /docs information architecture.
// The sidebar, breadcrumbs, prev/next pager and static params all derive from it.

export interface DocNavItem {
  title: string;
  href: string;
  badge?: "New" | "Soon" | "Alpha";
}

export interface DocNavSection {
  title: string;
  /** Extra breadcrumb level between the section and the page (e.g. "API Reference"). */
  crumb?: string;
  items: DocNavItem[];
  /** Renders the section as a highlighted card (used for MCP). */
  highlight?: boolean;
}

export interface DocTab {
  label: string;
  href?: string;
  soon?: boolean;
}

export const DOCS_BASE_URL = "https://quantwheel.com/api/v1";

export const DOCS_TABS: DocTab[] = [
  { label: "API Reference", href: "/docs" },
  { label: "Guides", soon: true },
  { label: "MCP", href: "/docs/mcp" },
  { label: "Skills", soon: true },
];

export const DOCS_NAV: DocNavSection[] = [
  {
    title: "Getting Started",
    items: [
      { title: "Overview", href: "/docs" },
      { title: "Quickstart", href: "/docs/quickstart" },
      { title: "Authentication", href: "/docs/authentication" },
      { title: "Rate Limits", href: "/docs/rate-limits" },
      { title: "Credits & Plans", href: "/docs/credits" },
    ],
  },
  {
    title: "Wheel",
    crumb: "API Reference",
    items: [
      { title: "Active Wheels", href: "/docs/api/wheel/wheels" },
      { title: "Journal", href: "/docs/api/wheel/journal" },
      { title: "Screener", href: "/docs/api/wheel/screener" },
      { title: "Optimal Roll", href: "/docs/api/wheel/optimal-roll" },
      { title: "Playground Trading", href: "/docs/api/wheel/playground" },
    ],
  },
  {
    title: "Directional Trading",
    crumb: "API Reference",
    items: [
      { title: "GEX, Vanna & Charm", href: "/docs/api/directional/exposure" },
      { title: "Heatmap", href: "/docs/api/directional/heatmap" },
      { title: "Flow", href: "/docs/api/directional/flow" },
      { title: "Earnings", href: "/docs/api/directional/earnings" },
      { title: "Historical Data", href: "/docs/api/directional/historical" },
    ],
  },
  {
    title: "Platform",
    crumb: "API Reference",
    items: [
      { title: "Alerts", href: "/docs/api/alerts" },
      { title: "AI Explanations", href: "/docs/api/ai-explanations" },
    ],
  },
  {
    title: "MCP",
    highlight: true,
    items: [{ title: "MCP Server", href: "/docs/mcp", badge: "New" }],
  },
];

export interface DocFlatEntry extends DocNavItem {
  section: DocNavSection;
}

export const DOCS_FLAT: DocFlatEntry[] = DOCS_NAV.flatMap((section) =>
  section.items.map((item) => ({ ...item, section })),
);

export function getDocEntry(href: string): DocFlatEntry | undefined {
  return DOCS_FLAT.find((entry) => entry.href === href);
}

export function getDocPager(href: string): {
  prev?: DocFlatEntry;
  next?: DocFlatEntry;
} {
  const index = DOCS_FLAT.findIndex((entry) => entry.href === href);
  if (index === -1) return {};
  return {
    prev: index > 0 ? DOCS_FLAT[index - 1] : undefined,
    next: index < DOCS_FLAT.length - 1 ? DOCS_FLAT[index + 1] : undefined,
  };
}

export function getBreadcrumbs(href: string): string[] {
  const entry = getDocEntry(href);
  if (!entry) return ["Docs"];
  const crumbs = ["Docs"];
  if (entry.section.crumb) crumbs.push(entry.section.crumb);
  crumbs.push(entry.section.title);
  if (entry.title !== entry.section.title) crumbs.push(entry.title);
  return crumbs;
}

export interface DocTocEntry {
  id: string;
  title: string;
  depth?: 2 | 3;
}

export interface DocPageMeta {
  title: string;
  description: string;
  toc: DocTocEntry[];
}
